/**
 * CreateReport - Reporter mode for filing scent reports (no login required)
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSocialStore } from '@/stores/useSocialStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { useLocationStore } from '@/stores/useLocationStore';
import type { CreateReportData } from '@/types/social';

interface CreateReportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreateReport({ isOpen, onClose, onSuccess }: CreateReportProps) {
  const { createReport } = useSocialStore();
  const airQuality = useAirQualityStore((state) => state.currentReading);
  const currentPosition = useLocationStore((state) => state.currentPosition);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateReportData>({
    scent_notes: '',
    description: '',
    location_name: '',
    aqi: airQuality?.aqi || undefined,
    pm25: airQuality?.pm25?.value || undefined,
    temperature: undefined,
    humidity: undefined,
  });

  // Helper function to get specific location name (POI first, then address)
  // Format: "Place/POI, Postcode, City"
  const getSpecificLocationName = async (lng: number, lat: number): Promise<string> => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;

    // Helper to extract context values
    const getContext = (context: Array<{ id: string; text: string }> | undefined, prefix: string) =>
      context?.find((c) => c.id.startsWith(prefix))?.text;

    // First try to get POI (shopping malls, restaurants, landmarks, etc.)
    try {
      const poiResponse = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=poi&limit=1`
      );
      const poiData = await poiResponse.json();

      if (poiData.features?.[0]) {
        const poi = poiData.features[0];
        const poiName = poi.text || poi.place_name;
        const postcode = getContext(poi.context, 'postcode');
        const place = getContext(poi.context, 'place');
        const locality = getContext(poi.context, 'locality');
        const city = place || locality;

        // Format: "Sunway Velocity, 55100, Kuala Lumpur"
        if (postcode && city) return `${poiName}, ${postcode}, ${city}`;
        if (city) return `${poiName}, ${city}`;
        return poiName;
      }
    } catch (e) {
      console.log('POI lookup failed, trying address');
    }

    // Fallback to neighborhood/locality with postcode and city
    try {
      const addressResponse = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=neighborhood,locality,place&limit=1`
      );
      const addressData = await addressResponse.json();

      if (addressData.features?.[0]) {
        const feature = addressData.features[0];
        const featureType = feature.id?.split('.')[0]; // e.g., "neighborhood", "locality", "place"
        const neighborhood = getContext(feature.context, 'neighborhood');
        const locality = getContext(feature.context, 'locality');
        const postcode = getContext(feature.context, 'postcode');
        const place = getContext(feature.context, 'place');
        const region = getContext(feature.context, 'region');

        // Determine the location name based on what we found
        let locationName = '';
        if (featureType === 'neighborhood') {
          locationName = feature.text;
        } else if (featureType === 'locality') {
          locationName = neighborhood || feature.text;
        } else if (featureType === 'place') {
          locationName = neighborhood || locality || feature.text;
        } else {
          locationName = neighborhood || locality || feature.text;
        }

        // Determine the city
        const city = place || (featureType !== 'locality' ? locality : null) || region;

        // Format: "Taman Maluri, 55100, Kuala Lumpur"
        if (locationName && postcode && city && locationName !== city && locationName !== postcode) {
          return `${locationName}, ${postcode}, ${city}`;
        }
        if (locationName && city && locationName !== city) {
          return `${locationName}, ${city}`;
        }
        if (locationName && postcode && locationName !== postcode) {
          return `${locationName}, ${postcode}`;
        }
        if (locationName) return locationName;
        if (city) return city;
        return feature.place_name;
      }
    } catch (e) {
      console.log('Address lookup failed');
    }

    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  };

  // Auto-detect location when form opens using existing map location
  useEffect(() => {
    if (isOpen && currentPosition && !formData.location_name) {
      setIsDetectingLocation(true);
      setUserCoords({ lat: currentPosition.lat, lng: currentPosition.lng });

      // Get specific location name (POI or address)
      getSpecificLocationName(currentPosition.lng, currentPosition.lat)
        .then((placeName) => {
          setFormData((prev) => ({ ...prev, location_name: placeName }));
          setIsDetectingLocation(false);
        })
        .catch(() => {
          setFormData((prev) => ({
            ...prev,
            location_name: `${currentPosition.lat.toFixed(4)}, ${currentPosition.lng.toFixed(4)}`,
          }));
          setIsDetectingLocation(false);
        });
    }
  }, [isOpen, currentPosition]);

  // Current timestamp
  const getCurrentTimestamp = () => {
    return new Date().toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const [timestamp] = useState(getCurrentTimestamp());

  // Scent suggestions for inspiration
  const scentSuggestions = [
    '🌸 Floral', '🌲 Pine', '🌊 Ocean breeze', '☕ Coffee',
    '🌧️ Fresh rain', '🍃 Cut grass', '🍞 Fresh bread', '🌺 Jasmine',
    '🍺 Beer', '🌳 Woods', '🌿 Herbs', '🔥 Smoke', '🏭 Industrial',
  ];

  const handleScentSuggestion = (scent: string) => {
    const cleanScent = scent.replace(/^[^\s]+\s/, ''); // Remove emoji
    setFormData((prev) => ({
      ...prev,
      scent_notes: prev.scent_notes
        ? `${prev.scent_notes}, ${cleanScent}`
        : cleanScent,
    }));
  };

  // Photo handling
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [locationError, setLocationError] = useState<string | null>(null);

  // Detect current location - uses existing map position first, falls back to geolocation
  const detectCurrentLocation = async () => {
    setIsDetectingLocation(true);
    setLocationError(null);

    // First try to use existing position from the map
    if (currentPosition) {
      try {
        setUserCoords({ lat: currentPosition.lat, lng: currentPosition.lng });
        const placeName = await getSpecificLocationName(currentPosition.lng, currentPosition.lat);
        setFormData((prev) => ({ ...prev, location_name: placeName }));
        setIsDetectingLocation(false);
        return;
      } catch {
        setFormData((prev) => ({
          ...prev,
          location_name: `${currentPosition.lat.toFixed(4)}, ${currentPosition.lng.toFixed(4)}`,
        }));
        setIsDetectingLocation(false);
        return;
      }
    }

    // Fallback: try browser geolocation
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            setUserCoords({ lat: latitude, lng: longitude });
            const placeName = await getSpecificLocationName(longitude, latitude);
            setFormData((prev) => ({ ...prev, location_name: placeName }));
          } catch {
            setFormData((prev) => ({
              ...prev,
              location_name: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`,
            }));
          }
          setIsDetectingLocation(false);
        },
        (error) => {
          console.error('Location error:', error);
          if (error.code === 1) {
            setLocationError('Location access denied. Please enter manually.');
          } else {
            setLocationError('Location unavailable. Please enter manually.');
          }
          setIsDetectingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setLocationError('Location not available. Please enter manually.');
      setIsDetectingLocation(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.scent_notes || !displayName.trim()) {
      setSubmitError('Please fill in your name and scent description');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Use stored coordinates if available, otherwise try currentPosition from map
      const coords = userCoords || (currentPosition ? { lat: currentPosition.lat, lng: currentPosition.lng } : null);

      // Create report and WAIT for database save (critical for incognito mode)
      await createReport({
        ...formData,
        latitude: coords?.lat,
        longitude: coords?.lng,
        description: `Reporter: ${displayName.trim()}\n${formData.description || ''}`,
      });

      // Show success after database save completes
      setShowSuccess(true);
      onSuccess?.();
      setIsSubmitting(false);

      // Close after showing success message
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        resetForm();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit report:', error);
      setSubmitError('Failed to submit report. Please try again.');
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      scent_notes: '',
      description: '',
      location_name: '',
      aqi: airQuality?.aqi || undefined,
      pm25: airQuality?.pm25?.value || undefined,
    });
    setDisplayName('');
    setPhotoPreview(null);
    setLocationError(null);
    setUserCoords(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'relative w-full sm:max-w-md bg-app-bg border border-glass-border',
              'rounded-t-3xl sm:rounded-2xl overflow-hidden',
              'max-h-[90vh] overflow-y-auto'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-app-bg/90 backdrop-blur-xl border-b border-glass-border p-4">
              <div className="flex items-center justify-between">
                <button onClick={onClose} className="text-text-secondary hover:text-white">
                  Cancel
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎤</span>
                  <span className="font-bold text-white">File Report</span>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.scent_notes || !displayName.trim() || isSubmitting}
                  className={cn(
                    'px-4 py-1.5 rounded-full font-semibold text-sm',
                    'bg-hud-primary text-black',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? 'Filing...' : 'Post'}
                </button>
              </div>

              {/* Timestamp */}
              <div className="mt-3 flex items-center justify-between text-xs text-text-secondary">
                <span>🕐 {timestamp}</span>
                <span>📍 Live Report</span>
              </div>

              {/* Error message */}
              {submitError && (
                <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-xs text-center">{submitError}</p>
                </div>
              )}
            </div>

            {/* Success overlay */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute inset-0 bg-app-bg/95 backdrop-blur-xl flex items-center justify-center z-10"
                >
                  <div className="text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.1 }}
                      className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center"
                    >
                      <span className="text-4xl">✓</span>
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-2">Report Filed!</h3>
                    <p className="text-text-secondary text-sm">Thanks for sharing your scent experience</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Your Display Name *
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name or nickname"
                  maxLength={30}
                  className={cn(
                    'w-full bg-glass-bg border border-glass-border rounded-xl p-3',
                    'text-white placeholder:text-text-secondary',
                    'focus:outline-none focus:border-hud-primary'
                  )}
                />
              </div>

              {/* Scent notes */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  What do you smell? *
                </label>
                <textarea
                  value={formData.scent_notes}
                  onChange={(e) => setFormData({ ...formData, scent_notes: e.target.value })}
                  placeholder="Fresh rain, jasmine, a hint of coffee from the nearby café..."
                  rows={3}
                  className={cn(
                    'w-full bg-glass-bg border border-glass-border rounded-xl p-3',
                    'text-white placeholder:text-text-secondary',
                    'focus:outline-none focus:border-hud-primary'
                  )}
                />
                {/* Suggestions */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {scentSuggestions.map((scent) => (
                    <button
                      key={scent}
                      onClick={() => handleScentSuggestion(scent)}
                      className="text-xs px-2 py-1 bg-slate-700/50 hover:bg-hud-primary/20 rounded-full text-text-secondary hover:text-white transition-colors"
                    >
                      {scent}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location with detect button */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  📍 Current location
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.location_name}
                    onChange={(e) => {
                      setFormData({ ...formData, location_name: e.target.value });
                      if (locationError) setLocationError(null);
                    }}
                    placeholder="Enter your location"
                    className={cn(
                      'flex-1 bg-glass-bg border border-glass-border rounded-xl p-3',
                      'text-white placeholder:text-text-secondary',
                      'focus:outline-none focus:border-hud-primary'
                    )}
                  />
                  <button
                    type="button"
                    onClick={detectCurrentLocation}
                    disabled={isDetectingLocation}
                    className={cn(
                      'px-4 py-3 rounded-xl',
                      'bg-hud-primary/20 border border-hud-primary/30',
                      'text-hud-primary text-sm font-medium',
                      'hover:bg-hud-primary/30 transition-colors',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'flex items-center gap-2'
                    )}
                  >
                    {isDetectingLocation ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                    <span className="hidden sm:inline">Detect</span>
                  </button>
                </div>
                {/* Location error message */}
                {locationError && (
                  <p className="mt-2 text-xs text-amber-400">{locationError}</p>
                )}
              </div>

              {/* Photo upload (optional) */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  📷 Add Photo <span className="text-text-secondary font-normal">(optional)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full h-40 object-cover rounded-xl"
                    />
                    <button
                      onClick={removePhoto}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      'w-full p-4 rounded-xl border-2 border-dashed border-glass-border',
                      'text-text-secondary hover:border-hud-primary/50 hover:text-white',
                      'transition-colors flex items-center justify-center gap-2'
                    )}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Tap to add photo</span>
                  </button>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Additional notes <span className="text-text-secondary font-normal">(optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Any additional observations..."
                  rows={2}
                  className={cn(
                    'w-full bg-glass-bg border border-glass-border rounded-xl p-3',
                    'text-white placeholder:text-text-secondary',
                    'focus:outline-none focus:border-hud-primary'
                  )}
                />
              </div>

              {/* Current conditions */}
              {airQuality && (
                <div className="p-3 bg-slate-800/50 rounded-xl">
                  <div className="text-xs text-text-secondary mb-2 uppercase tracking-wider">
                    Current Conditions (auto-attached)
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-emerald-400">AQI {airQuality.aqi}</span>
                    <span className="text-text-secondary">PM2.5: {airQuality.pm25?.value} µg/m³</span>
                  </div>
                </div>
              )}

              {/* Premium Share Preview Card */}
              <div>
                <div className="text-xs text-text-secondary mb-3 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                  Share Preview
                </div>

                {/* Glassmorphic Card */}
                <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#050a0f] via-[#0a1520] to-[#050a0f]" />

                  {/* Decorative orbs */}
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-fuchsia-500/20 rounded-full blur-3xl" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-cyan-400/5 rounded-full blur-2xl" />

                  {/* Wave pattern overlay */}
                  <div className="absolute inset-0 opacity-10">
                    <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none">
                      <path d="M0,100 C100,150 200,50 400,100 L400,400 L0,400 Z" fill="url(#waveGrad)" />
                      <defs>
                        <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#00ffd0" />
                          <stop offset="100%" stopColor="#00b4d8" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Card content */}
                  <div className="relative p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        {/* Blurp.ie Logo */}
                        <div className="relative">
                          <div className="w-12 h-12 rounded-2xl bg-black flex items-center justify-center border border-cyan-500/30 shadow-lg shadow-cyan-500/20 overflow-hidden">
                            <img src="/logo.png" alt="Blurp.ie" className="w-10 h-10 object-contain" />
                          </div>
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-[#050a0f] animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-cyan-200 bg-clip-text text-transparent">
                            BLURP.IE
                          </h3>
                          <p className="text-[10px] text-cyan-500/60 uppercase tracking-[0.2em]">Better Air, Better Runs</p>
                        </div>
                      </div>
                      <div className="px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                        <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wider">Live</span>
                      </div>
                    </div>

                    {/* Main scent content */}
                    <div className="relative mb-6 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                      <div className="absolute -top-3 left-4">
                        <span className="text-4xl text-cyan-400/50">"</span>
                      </div>
                      <p className="text-xl font-semibold text-white leading-relaxed pt-2 pb-1">
                        {formData.scent_notes || 'Describe what you smell...'}
                      </p>
                      <div className="absolute -bottom-3 right-4">
                        <span className="text-4xl text-cyan-400/50">"</span>
                      </div>
                    </div>

                    {/* Info pills */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {/* Location pill */}
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20">
                        <svg className="w-4 h-4 text-rose-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-rose-200 truncate max-w-[140px]">{formData.location_name || 'Location'}</span>
                      </div>

                      {/* Time pill */}
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20">
                        <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-purple-200">{timestamp}</span>
                      </div>

                      {/* AQI pill */}
                      {formData.aqi && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-sm font-medium text-emerald-300">AQI {formData.aqi}</span>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 border border-white/10 flex items-center justify-center">
                          <span className="text-lg">👤</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{displayName || 'Anonymous'}</p>
                          <p className="text-[10px] text-slate-500">Scent Reporter</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500 mb-1">#InternetOfScent</p>
                        <p className="text-[10px] font-medium bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">blurp.ie</p>
                      </div>
                    </div>
                  </div>

                  {/* Bottom gradient line */}
                  <div className="h-1 bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-cyan-500" />
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
